import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload, Save } from 'lucide-react'

import { createContainer } from '@/api/containersAPI'
import { getErrorMessage } from '@/utils/errorHandlers'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/newRedesign/primitives/Dialog'
import { Button } from '@/components/newRedesign/primitives/Button'
import { cn } from '@/lib/utils'

import { StepIndicator } from './StepIndicator'
import { DetailsStep } from './steps/DetailsStep'
import { DocumentsStep } from './steps/DocumentsStep'
import { CostsStep } from './steps/CostsStep'

export interface ContainerDocument {
  id?: string
  name: string
  observations?: string
  file?: File
}

export interface ContainerCost {
  id?: string
  fixedCostCategoryId: number | null
  registeredPrice: {
    amount: number
    codeCurrency: string
  }
  type: string
}

export interface CreateContainerFormData {
  // Step 1 - Details
  name: string
  countryFromId?: number
  originId?: number
  destinyId?: number
  categories?: number[]
  code?: string
  trackingNumber?: string
  observations?: string

  // Step 2 - Documents (handled separately)
  documents: ContainerDocument[]

  // Step 3 - Costs
  costs: ContainerCost[]
}

interface CreateContainerWizardProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  onSuccess?: (_containerId: number) => void
}

export function CreateContainerWizard({
  open,
  onOpenChange,
  onSuccess,
}: CreateContainerWizardProps) {
  const queryClient = useQueryClient()
  const [currentStep, setCurrentStep] = useState(1)
  const [documents, setDocuments] = useState<ContainerDocument[]>([])
  const [costs, setCosts] = useState<ContainerCost[]>([])
  const [isUploadingDocument, setIsUploadingDocument] = useState(false)
  const [isEditingDocument, setIsEditingDocument] = useState(false)
  const [isAddingCost, setIsAddingCost] = useState(false)
  const [pendingDocumentsCount, setPendingDocumentsCount] = useState(0)
  const [documentFormRef, setDocumentFormRef] = useState<{
    submit: () => void
    cancel: () => void
  } | null>(null)
  const [costFormRef, setCostFormRef] = useState<{
    submit: () => void
    cancel: () => void
  } | null>(null)

  const { control, handleSubmit, reset } = useForm<CreateContainerFormData>({
    defaultValues: {
      name: '',
      countryFromId: undefined,
      originId: undefined,
      destinyId: undefined,
      categories: [],
      code: '',
      trackingNumber: '',
      observations: '',
      documents: [],
      costs: [],
    },
  })

  // Reset form and state when dialog closes
  useEffect(() => {
    if (!open) {
      setCurrentStep(1)
      setDocuments([])
      setCosts([])
      setIsUploadingDocument(false)
      setIsEditingDocument(false)
      setIsAddingCost(false)
      setPendingDocumentsCount(0)
      setDocumentFormRef(null)
      setCostFormRef(null)
      reset()
    }
  }, [open, reset])

  // Mutation for creating container
  const createContainerMutation = useMutation({
    mutationFn: async (data: CreateContainerFormData) => {
      // Prepare container data for API
      const containerData = {
        name: data.name,
        countryFromId: data.countryFromId,
        originId: data.originId,
        destinyId: data.destinyId,
        categories: data.categories || [],
        ...(data.code && data.code.trim() !== '' && { code: data.code }),
        trackingNumber:
          data.trackingNumber && data.trackingNumber.trim() !== ''
            ? Number(data.trackingNumber)
            : undefined,
        observations: data.observations,
        listCosts:
          costs.length > 0
            ? costs.map((cost) => ({
                registeredPrice: cost.registeredPrice,
                observations: null,
                type: cost.type,
                fixedCostCategoryId: cost.fixedCostCategoryId,
              }))
            : undefined,
      }

      const response = await createContainer(containerData, 'v2')

      // Extract container ID from response
      let containerId: number | null = null

      if (response?.id) {
        containerId = response.id
      } else if (response?.data?.id) {
        containerId = response.data.id
      }

      if (!containerId) {
        throw new Error('No se pudo obtener el ID del contenedor creado')
      }

      // Upload documents if any
      if (documents && documents.length > 0) {
        const { postMedia } = await import('@/api/mediaAPI')
        const { postContainerDocuments } = await import('@/api/containersAPI')

        for (const document of documents) {
          // First, upload the file to S3 to get the path
          if (document.file) {
            const fileExtension =
              document.file.name.split('.').pop()?.toLowerCase() || 'png'
            const mediaParams = {
              params: {
                documentType: 'IMAGE',
                size: Math.ceil(document.file.size / (1024 * 1024)) || 1,
                ext: fileExtension,
                isPrivate: true,
                name: document.name || document.file.name,
              },
              file: document.file,
            }

            const path = await postMedia(mediaParams)

            // Then create the document record with the S3 path
            const documentPayload = {
              name: document.name || 'Documento',
              path: path,
              observations: document.observations || '',
            }

            await postContainerDocuments({
              id: containerId,
              params: documentPayload,
            })
          }
        }
      }

      return containerId
    },
    onSuccess: (containerId) => {
      toast.success('Contenedor creado exitosamente')
      queryClient.invalidateQueries({ queryKey: ['containers'] })
      onSuccess?.(containerId)
      onOpenChange(false)
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error)
      toast.error(errorMessage || 'Error al crear el contenedor')
    },
  })

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const onSubmit = (data: CreateContainerFormData) => {
    // If in upload or edit mode on step 2, submit the document form
    if (
      currentStep === 2 &&
      (isUploadingDocument || isEditingDocument) &&
      documentFormRef
    ) {
      documentFormRef.submit()
      return
    }

    // If in add cost mode on step 3, submit the cost form
    if (currentStep === 3 && isAddingCost && costFormRef) {
      costFormRef.submit()
      return
    }

    // If on step 1, validate required fields and move to next step
    if (currentStep === 1) {
      if (!data.name) {
        toast.error('El nombre es requerido')
        return
      }

      // Validar que origen y destino no sean los mismos
      if (data.originId && data.destinyId && data.originId === data.destinyId) {
        toast.error('El origen y destino no pueden ser los mismos')
        return
      }

      handleNext()
      return
    }

    // If on step 2, move to next step
    if (currentStep === 2) {
      handleNext()
      return
    }

    // If on step 3, submit the form
    if (currentStep === 3) {
      createContainerMutation.mutate({ ...data, documents, costs })
    }
  }

  const handlePreviousOrCancel = () => {
    // If in upload or edit mode on step 2, cancel the upload/edit
    if (
      currentStep === 2 &&
      (isUploadingDocument || isEditingDocument) &&
      documentFormRef
    ) {
      documentFormRef.cancel()
      return
    }

    // If in add cost mode on step 3, cancel the cost form
    if (currentStep === 3 && isAddingCost && costFormRef) {
      costFormRef.cancel()
      return
    }

    // Otherwise, go to previous step
    handlePrevious()
  }

  const isLoading = createContainerMutation.isPending

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className={cn(
          'flex w-[calc(100vw-2rem)] flex-col gap-0',
          'h-[min(calc(100vh-2rem),750px)]',
          'bg-[#FAFAFA] p-0',
          'shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)]',
          'rounded-lg',
          'sm:w-[calc(100vw-4rem)] sm:max-w-[700px]',
          'md:max-w-[800px]',
          'lg:max-w-[900px]'
        )}
      >
        {/* Dialog Header */}
        <DialogHeader className="shrink-0 border-b border-[#D4D4D4] px-4 py-3 sm:py-4">
          <DialogTitle className="text-lg font-semibold leading-tight tracking-[-0.02em] text-[#262626] sm:text-xl">
            Nuevo Contenedor
          </DialogTitle>
        </DialogHeader>

        {/* Step Indicator */}
        <StepIndicator currentStep={currentStep} totalSteps={3} />

        {/* Form Content */}
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* Step Content - fixed height with scroll */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-[#FAFAFA] px-4 py-4">
            {currentStep === 1 && <DetailsStep control={control} />}
            {currentStep === 2 && (
              <DocumentsStep
                documents={documents}
                onDocumentsChange={setDocuments}
                onEditModeChange={setIsEditingDocument}
                onFormRefChange={setDocumentFormRef}
                onPendingDocumentsCountChange={setPendingDocumentsCount}
                onUploadModeChange={setIsUploadingDocument}
              />
            )}
            {currentStep === 3 && (
              <CostsStep
                costs={costs}
                onCostModeChange={setIsAddingCost}
                onCostsChange={setCosts}
                onFormRefChange={setCostFormRef}
              />
            )}
          </div>

          {/* Footer with Navigation Buttons */}
          <DialogFooter className="shrink-0 border-t border-[#D4D4D4] px-4 py-3 sm:py-4">
            {/* When in add document, edit document, or add cost mode, show different layout */}
            {(currentStep === 2 &&
              (isUploadingDocument || isEditingDocument)) ||
            (currentStep === 3 && isAddingCost) ? (
              <div className="flex w-full items-center justify-between">
                <Button
                  disabled={isLoading}
                  onClick={handlePreviousOrCancel}
                  size="md"
                  type="button"
                  variant="outline"
                >
                  Cancelar
                </Button>
                <Button
                  className="bg-slate-800 hover:bg-slate-700"
                  disabled={
                    isLoading ||
                    (currentStep === 2 &&
                      isUploadingDocument &&
                      pendingDocumentsCount === 0)
                  }
                  icon={
                    currentStep === 2 && isUploadingDocument ? (
                      <Upload className="h-4 w-4" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )
                  }
                  size="md"
                  type="submit"
                >
                  {isLoading
                    ? 'Guardando...'
                    : currentStep === 2 && isUploadingDocument
                      ? pendingDocumentsCount === 1
                        ? 'Subir archivo'
                        : 'Subir archivos'
                      : 'Guardar'}
                </Button>
              </div>
            ) : (
              <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center justify-start">
                  {currentStep > 1 && (
                    <Button
                      disabled={isLoading}
                      onClick={handlePreviousOrCancel}
                      size="md"
                      type="button"
                      variant="outline"
                    >
                      Anterior
                    </Button>
                  )}
                </div>
                <div className="flex items-center justify-end gap-3">
                  <Button
                    className="bg-slate-800 hover:bg-slate-700"
                    disabled={isLoading}
                    size="md"
                    type="submit"
                  >
                    {isLoading
                      ? 'Guardando...'
                      : currentStep === 3
                        ? 'Finalizar'
                        : 'Continuar'}
                  </Button>
                </div>
              </div>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
